import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { tmsCarriersApi, CreateCarrierDto, CarrierType, ServiceType } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

const CARRIER_TYPES: { value: CarrierType; label: string }[] = [
  { value: 'LTL', label: 'LTL (Less Than Truckload)' },
  { value: 'FTL', label: 'FTL (Full Truckload)' },
  { value: 'PARCEL', label: 'Parcel' },
  { value: 'AIR', label: 'Air Freight' },
  { value: 'OCEAN', label: 'Ocean Freight' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'INTERMODAL', label: 'Intermodal' },
  { value: 'COURIER', label: 'Courier' },
  { value: 'SPECIALIZED', label: 'Specialized' },
];

const SERVICE_TYPES: { value: ServiceType; label: string }[] = [
  { value: 'STANDARD', label: 'Standard' },
  { value: 'EXPEDITED', label: 'Expedited' },
  { value: 'OVERNIGHT', label: 'Overnight' },
  { value: 'SAME_DAY', label: 'Same Day' },
  { value: 'ECONOMY', label: 'Economy' },
  { value: 'DEDICATED', label: 'Dedicated' },
  { value: 'TEMPERATURE_CONTROLLED', label: 'Temperature Controlled' },
  { value: 'HAZMAT', label: 'Hazmat' },
  { value: 'OVERSIZED', label: 'Oversized' },
];

interface CarrierFormData {
  code: string;
  name: string;
  legalName: string;
  type: CarrierType;
  primaryContact: string;
  email: string;
  phone: string;
  website: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  mcNumber: string;
  dotNumber: string;
  scac: string;
  equipmentTypes: string;
  hazmatCertified: boolean;
  temperatureControlled: boolean;
  paymentTermsDays: number;
}

const emptyFormData: CarrierFormData = {
  code: '',
  name: '',
  legalName: '',
  type: 'FTL',
  primaryContact: '',
  email: '',
  phone: '',
  website: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'US',
  mcNumber: '',
  dotNumber: '',
  scac: '',
  equipmentTypes: '',
  hazmatCertified: false,
  temperatureControlled: false,
  paymentTermsDays: 30,
};

export default function CarriersNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<CarrierFormData>(emptyFormData);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<ServiceType[]>(['STANDARD']);
  const [saving, setSaving] = useState(false);

  const toggleServiceType = (type: ServiceType) => {
    setSelectedServiceTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.code || !formData.name || !formData.type) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const payload: CreateCarrierDto = {
        companyId: user!.companyId,
        code: formData.code,
        name: formData.name,
        legalName: formData.legalName || undefined,
        type: formData.type,
        primaryContact: formData.primaryContact || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        website: formData.website || undefined,
        address: {
          street: formData.street || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined,
          country: formData.country || 'US',
        },
        mcNumber: formData.mcNumber || undefined,
        dotNumber: formData.dotNumber || undefined,
        scac: formData.scac || undefined,
        serviceTypes: selectedServiceTypes,
        equipmentTypes: formData.equipmentTypes
          ? formData.equipmentTypes.split(',').map((s) => s.trim())
          : [],
        hazmatCertified: formData.hazmatCertified,
        temperatureControlled: formData.temperatureControlled,
        paymentTermsDays: formData.paymentTermsDays,
      };

      await tmsCarriersApi.create(payload);
      toast.success('Carrier created successfully');
      navigate('/tms/carriers');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Add Carrier"
        description="Details required to register a transportation provider and its service lanes."
        actions={
          <Button variant="outline" onClick={() => navigate('/tms/carriers')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Carriers
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Basic Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="CARR001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData({ ...formData, type: value as CarrierType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CARRIER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Carrier Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="ABC Logistics"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="legalName">Legal Name</Label>
              <Input
                id="legalName"
                value={formData.legalName}
                onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                placeholder="ABC Logistics Inc."
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Contact Information</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryContact">Primary Contact</Label>
                <Input
                  id="primaryContact"
                  value={formData.primaryContact}
                  onChange={(e) => setFormData({ ...formData, primaryContact: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@abclogistics.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://abclogistics.com"
                />
              </div>
            </div>
          </div>

          {/* Identification */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Identification</h3>
            <Separator />

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mcNumber">MC Number</Label>
                <Input
                  id="mcNumber"
                  value={formData.mcNumber}
                  onChange={(e) => setFormData({ ...formData, mcNumber: e.target.value })}
                  placeholder="MC-123456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dotNumber">DOT Number</Label>
                <Input
                  id="dotNumber"
                  value={formData.dotNumber}
                  onChange={(e) => setFormData({ ...formData, dotNumber: e.target.value })}
                  placeholder="DOT-123456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scac">SCAC Code</Label>
                <Input
                  id="scac"
                  value={formData.scac}
                  onChange={(e) => setFormData({ ...formData, scac: e.target.value.toUpperCase() })}
                  placeholder="ABCD"
                />
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Capabilities</h3>
            <Separator />

            <div>
              <Label className="mb-2 block">Service Types</Label>
              <div className="flex flex-wrap gap-2">
                {SERVICE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => toggleServiceType(type.value)}
                    className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                      selectedServiceTypes.includes(type.value)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted bg-background text-muted-foreground hover:border-primary/50'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="equipmentTypes">Equipment Types (comma-separated)</Label>
              <Input
                id="equipmentTypes"
                value={formData.equipmentTypes}
                onChange={(e) => setFormData({ ...formData, equipmentTypes: e.target.value })}
                placeholder="Dry Van, Flatbed, Refrigerated"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Hazmat Certified</Label>
                <p className="text-sm text-muted-foreground">
                  Carrier is certified to handle hazardous materials
                </p>
              </div>
              <Switch
                checked={formData.hazmatCertified}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, hazmatCertified: checked })
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Temperature Controlled</Label>
                <p className="text-sm text-muted-foreground">
                  Carrier has temperature-controlled transport
                </p>
              </div>
              <Switch
                checked={formData.temperatureControlled}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, temperatureControlled: checked })
                }
              />
            </div>
          </div>

          {/* Address */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Address</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="street">Street</Label>
              <Input
                id="street"
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Chicago"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="IL"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="zipCode">ZIP Code</Label>
                <Input
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="60601"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="US"
                />
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Payment Terms</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="paymentTermsDays">Payment Terms (days)</Label>
              <Input
                id="paymentTermsDays"
                type="number"
                min="0"
                value={formData.paymentTermsDays}
                onChange={(e) =>
                  setFormData({ ...formData, paymentTermsDays: parseInt(e.target.value) || 30 })
                }
                placeholder="30"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/tms/carriers')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Add Carrier'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
